import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, MessageCircle, Send } from "lucide-react";
import Reveal from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONTACTS = [
  { icon: Phone, label: "Call us", value: "+91 70215 04681", href: "tel:+917021504681", testid: "contact-phone-link" },
  { icon: MessageCircle, label: "WhatsApp", value: "+91 70215 04681", href: "https://wa.me/917021504681", testid: "contact-whatsapp-link" },
  { icon: Mail, label: "Email", value: "ppsllp2026@gmail.com", href: "mailto:ppsllp2026@gmail.com", testid: "contact-email-link" },
  { icon: MapPin, label: "Store", value: "Unit 16, Neelkanth Udhyog Bhavan, Opp 72 Corp, Sakivihar Road, Sakinaka, Andheri East, Mumbai, Maharashtra 400072", href: undefined, testid: "contact-address" },
  { icon: Clock, label: "Hours", value: "Mon–Sat, 9:30 AM – 8:00 PM", href: undefined, testid: "contact-hours" },
];

const EMPTY = { name: "", phone: "", email: "", service: "Document Printing", message: "" };

export default function EnquiryForm() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [member, setMember] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then(({ data }) => {
        setMember(data);
        setForm((f) => ({ ...f, name: data.name || f.name, email: data.email || f.email }));
      })
      .catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry received — we'll call you back within the hour.");
      setForm(EMPTY);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send. Please call us instead.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-ink border border-edge rounded-sm px-4 py-3.5 text-base text-paper placeholder:text-mist/50 focus:outline-none focus:border-cyanbrand transition-colors duration-300";

  return (
    <section id="contact" className="py-20 lg:py-28" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Reveal>
            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-cyanbrand">/ Contact</span>
            <h2 className="mt-4 font-display font-semibold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-paper leading-[1.05]">
              Tell us what
              <br /> needs printing.
            </h2>
            <p className="mt-6 text-sm sm:text-base text-mist leading-relaxed max-w-md">
              Drop the details and we&apos;ll call back with a quote within the hour.
              Pay at the store or on delivery — no online payment needed.
            </p>
          </Reveal>
          <Reveal delay={1}>
            <ul className="mt-10 space-y-5">
              {CONTACTS.map(({ icon: Icon, label, value, href, testid }) => (
                <li key={label} className="flex items-start gap-4">
                  <span className="w-10 h-10 shrink-0 grid place-items-center border border-edge bg-surface rounded-sm text-cyanbrand">
                    <Icon size={16} />
                  </span>
                  <span>
                    <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-mist">{label}</span>
                    {href ? (
                      <a href={href} data-testid={testid} className="block mt-1 text-sm text-paper hover:text-cyanbrand transition-colors">
                        {value}
                      </a>
                    ) : (
                      <span data-testid={testid} className="block mt-1 text-sm text-paper">{value}</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={1}>
            <motion.form
              onSubmit={submit}
              data-testid="enquiry-form"
              className="border border-edge bg-surface rounded-sm p-6 sm:p-10 grid grid-cols-1 sm:grid-cols-2 gap-5"
            >
              <div className="sm:col-span-2 flex items-center justify-between flex-wrap gap-2">
                <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-mist">
                  Enquiry form{member ? <span className="text-cyanbrand"> · signed in as {member.email}</span> : null}
                </span>
                <span className="font-mono text-[10px] text-yel">PAY AT STORE / ON DELIVERY</span>
              </div>
              <div>
                <label htmlFor="enq-name" className="block font-mono text-[11px] uppercase tracking-[0.2em] text-mist mb-2">Name *</label>
                <input id="enq-name" required data-testid="enquiry-name-input" className={inputCls} value={form.name} onChange={set("name")} placeholder="Your full name" />
              </div>
              <div>
                <label htmlFor="enq-phone" className="block font-mono text-[11px] uppercase tracking-[0.2em] text-mist mb-2">Phone *</label>
                <input id="enq-phone" required type="tel" data-testid="enquiry-phone-input" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+91 ..." />
              </div>
              <div>
                <label htmlFor="enq-email" className="block font-mono text-[11px] uppercase tracking-[0.2em] text-mist mb-2">Email</label>
                <input id="enq-email" type="email" data-testid="enquiry-email-input" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@company.com" />
              </div>
              <div>
                <label htmlFor="enq-service" className="block font-mono text-[11px] uppercase tracking-[0.2em] text-mist mb-2">Service</label>
                <select id="enq-service" data-testid="enquiry-service-select" className={inputCls} value={form.service} onChange={set("service")}>
                  {["Document Printing", "Spiral Binding", "Wiro Binding", "Thesis / Hard Cover", "Staple & Pin Finishing", "UV Stickers", "UV Direct Protected Print", "Corporate Gift Combos (Catalog)", "Something else"].map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="enq-message" className="block font-mono text-[11px] uppercase tracking-[0.2em] text-mist mb-2">What do you need?</label>
                <textarea id="enq-message" rows={4} data-testid="enquiry-message-input" className={`${inputCls} resize-none`} value={form.message} onChange={set("message")} placeholder="e.g. 3 copies of a 60-page report, spiral bound, delivered to BKC by Friday" />
              </div>
              <div className="sm:col-span-2">
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="enquiry-submit-button"
                  className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-cyanbrand text-ink font-bold text-sm px-8 py-4 rounded-sm hover:bg-paper transition-colors duration-300 disabled:opacity-50"
                >
                  {sending ? "Sending..." : "Send Enquiry"}
                  <Send size={15} className="transition-transform duration-300 group-hover:translate-x-0.5" />
                </button>
              </div>
            </motion.form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
